/**
 * 対象SKU入力（改行区切りのUUID）のパース。
 *
 * フォーム側から切り出しているのは、行番号付きのエラー表示・重複の畳み込みという
 * 判断を単体テストで固定したいため（`seller-tag/lib/tagNormalize.ts` と同じ流儀）。
 */

/**
 * 画面に返す不正行の件数の上限。
 *
 * CSV アップロードで 30 万行を扱うため、全件を返すと `invalidLines.map()` の描画で
 * DOM が破綻する。バックエンド側の `MAX_REPORTED_INVALID_LINES`（Fargate）と同じ値にする。
 */
export const MAX_REPORTED_INVALID_LINES = 20;

/** UUID として解釈できなかった行 */
export interface InvalidSkuLine {
  /** 1 始まりの行番号。エラー表示でユーザーが該当行を探せるようにする */
  lineNumber: number;
  value: string;
}

export interface ParsedSkuLines {
  /** 正規形（小文字）に揃え、重複を除いた UUID */
  skus: string[];
  /**
   * UUID として読めなかった行。`MAX_REPORTED_INVALID_LINES` 件で打ち切る。
   * 30万行すべてが不正なときに全件を描画すると DOM が破綻するため
   */
  invalidLines: InvalidSkuLine[];
  /** 打ち切る前の不正行の総件数。`invalidLines.length` 以上になる */
  invalidLineTotal: number;
  /** 重複として除いた件数。0 より大きいときだけ画面で重複を伝える */
  duplicateCount: number;
}

/** 小文字ハイフン区切りの正規形 */
const HYPHENATED_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/** ハイフンを含まない32桁の16進文字列 */
const BARE_UUID = /^[0-9a-f]{32}$/;

/**
 * 正規形に揃えた UUID を返す。UUID として読めなければ null。
 *
 * CSV ファイルのサンプル検証（`TargetSkuFileInput`）と textarea の両方で
 * 受理する表記を一致させるため、ここだけが判定を持つ。
 */
export function toCanonicalSkuUuid(line: string): string | null {
  const lowered = line.toLowerCase();
  if (HYPHENATED_UUID.test(lowered)) {
    return lowered;
  }
  if (BARE_UUID.test(lowered)) {
    return [
      lowered.slice(0, 8),
      lowered.slice(8, 12),
      lowered.slice(12, 16),
      lowered.slice(16, 20),
      lowered.slice(20),
    ].join('-');
  }
  return null;
}

/**
 * 改行区切りの入力を UUID の配列に変換する。
 *
 * 空行は飛ばすが行番号には数える（ユーザーが不正な行を探せるようにするため）。
 * ハイフン区切りの正規形とハイフン無し32桁の16進文字列を受け、小文字のハイフン区切りへ
 * 揃えてから、順序を保ったまま重複を除く（バックエンドの `Uuid::parse_str` が受理する
 * 表記を画面側で拒否しないため）。
 */
export function parseSkuLines(text: string): ParsedSkuLines {
  const skus: string[] = [];
  const invalidLines: InvalidSkuLine[] = [];
  const seen = new Set<string>();
  let validCount = 0;
  let invalidLineTotal = 0;

  text.split('\n').forEach((rawLine, index) => {
    // CRLF の \r と、貼り付けで混入するインデント・全角空白をここで落とす
    const line = rawLine.trim();
    if (line === '') {
      return;
    }

    const canonical = toCanonicalSkuUuid(line);
    if (canonical === null) {
      invalidLineTotal += 1;
      // 表示用の打ち切りは先頭から。後ろの行だけ残すとユーザーが直す順序を追えない
      if (invalidLines.length < MAX_REPORTED_INVALID_LINES) {
        invalidLines.push({ lineNumber: index + 1, value: line });
      }
      return;
    }

    validCount += 1;
    if (seen.has(canonical)) {
      return;
    }
    seen.add(canonical);
    skus.push(canonical);
  });

  return { skus, invalidLines, invalidLineTotal, duplicateCount: validCount - skus.length };
}
